/**
 * Speed profiles: the UNVALIDATED base values in config/robot_profile.json are
 * scaled by a named profile multiplier for game pacing. Acceleration is derived
 * so the robot reaches 90% of cruise speed in ~0.4 s at every profile.
 */

import {
  ACCEL_CAP,
  ACCEL_PER_CRUISE,
  DECEL_CAP,
  DECEL_PER_ACCEL,
  DEFAULT_SPEED_PROFILE,
  INPUT_SMOOTHING_TAU,
  MISSION_SPEED_PROFILE,
  POST_PASS_BOOST,
  SPEED_PROFILES,
  TUNING_DEFAULTS,
  TUNING_SENSITIVITY_RANGE,
  TUNING_SPEED_RANGE,
  YAW_GAIN,
  type SpeedProfileName,
} from "./constants";
import type { RobotProfile, Tuning } from "./types";

export interface EffectiveProfile {
  /** Profile name + multiplier, recorded with every episode. */
  speed_profile: SpeedProfileName;
  multiplier: number;
  /** Player tuning actually applied (clamped), recorded with every episode. */
  tuning: Tuning;
  /** Steering gain (rad/s per rad of heading error), scaled by sensitivity. */
  steer_gain: number;
  /** Input smoothing time constant (s), shrunk by sensitivity. */
  input_smoothing_tau_s: number;
  /** Autonomous cruise speed (m/s). */
  cruise_mps: number;
  /** Post-pass boost cruise (m/s), capped by max_speed_mps. */
  boost_mps: number;
  max_speed_mps: number;
  max_accel_mps2: number;
  max_decel_mps2: number;
  max_yaw_rate_radps: number;
  control_latency_ms: number;
  safety_ttc_s: number;
  width_m: number;
  length_m: number;
}

/** Resolve a profile name; the default depends on the level (mission = arcade). */
export function resolveSpeedProfile(name?: string | null, level?: string | null): SpeedProfileName {
  if (name && name in SPEED_PROFILES) return name as SpeedProfileName;
  return level === "mission" ? MISSION_SPEED_PROFILE : DEFAULT_SPEED_PROFILE;
}

function clampNum(v: number, lo: number, hi: number): number {
  return Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : (lo + hi) / 2;
}

/** Clamp user tuning into the supported ranges (defaults when missing). */
export function resolveTuning(t?: Partial<Tuning> | null): Tuning {
  return {
    sensitivity: clampNum(
      t?.sensitivity ?? TUNING_DEFAULTS.sensitivity,
      TUNING_SENSITIVITY_RANGE.min,
      TUNING_SENSITIVITY_RANGE.max,
    ),
    speedScale: clampNum(
      t?.speedScale ?? TUNING_DEFAULTS.speedScale,
      TUNING_SPEED_RANGE.min,
      TUNING_SPEED_RANGE.max,
    ),
  };
}

export function effectiveProfile(
  base: RobotProfile,
  name: SpeedProfileName = DEFAULT_SPEED_PROFILE,
  tuningIn?: Partial<Tuning> | null,
): EffectiveProfile {
  const tuning = resolveTuning(tuningIn);
  // Speed profile multiplier x player speed scale.
  const m = SPEED_PROFILES[name] * tuning.speedScale;
  const cruise = base.nominal_speed_mps * m;
  const maxSpeed = Math.max(base.max_speed_mps * m, cruise * POST_PASS_BOOST);
  // Physical accel/decel caps apply to the arcade profile only (ADR-022):
  // lab profiles keep ADR-017's rule exactly, at every tuning.
  const capped = name === "arcade";
  // A MEASURED base (ADR-074) carries real accelerations, so it is used as
  // written instead of the "90% of cruise in 0.4 s" pacing rule: the real
  // robot reaches 63% of a step in ~0.9 s, about five times slower than that
  // rule assumes. The placeholder base keeps the rule, byte for byte, so
  // every pinned trajectory and every existing test is unaffected.
  const measured = base._status === "MEASURED";
  const accel = measured
    ? base.max_accel_mps2 * m
    : capped
      ? Math.min(ACCEL_CAP, ACCEL_PER_CRUISE * cruise)
      : ACCEL_PER_CRUISE * cruise;
  const decel = measured
    ? base.max_decel_mps2 * m
    : capped
      ? Math.min(DECEL_CAP, DECEL_PER_ACCEL * accel)
      : DECEL_PER_ACCEL * accel;
  return {
    speed_profile: name,
    multiplier: m,
    tuning,
    steer_gain: YAW_GAIN * tuning.sensitivity,
    input_smoothing_tau_s: INPUT_SMOOTHING_TAU / tuning.sensitivity,
    cruise_mps: cruise,
    boost_mps: Math.min(cruise * POST_PASS_BOOST, maxSpeed),
    max_speed_mps: maxSpeed,
    max_accel_mps2: accel,
    max_decel_mps2: decel,
    max_yaw_rate_radps: base.max_yaw_rate_radps * m,
    control_latency_ms: base.control_latency_ms,
    safety_ttc_s: base.safety_ttc_s,
    width_m: base.width_m,
    length_m: base.length_m,
  };
}
